import { Themed, THEMES } from "@components/atoms/Themed"
import { ScrollReveal } from "@components/motion/Transitioned"
import { useGSAP } from "@gsap/react"
import DrawSVGPlugin from "gsap/dist/DrawSVGPlugin"
import { cn } from "@utils/tw"
import { defineBlock, EditableText, InnerBlocks, useInnerBlocks } from "eddev/blocks"
import { Fragment, useMemo, useRef, useState } from "react"
import slugify from "slugify"
import { useIntersectionObserver, useWindowSize } from "usehooks-ts"
import gsap from "gsap"

if (env.client) {
  gsap.registerPlugin(useGSAP)
  gsap.registerPlugin(DrawSVGPlugin)
  gsap.config({
    nullTargetWarn: false,
  })
}

export const meta: BlockMeta = {
  title: "Sticky Sections",
  icon: "block-default",
  tags: ["#page"],
}

export default defineBlock("content/sticky-sections", (props) => {
  const blocks = useInnerBlocks()
  const [hovered, setHovered] = useState<number | null>(null)
  const outerRef = useRef<HTMLDivElement>(null)
  const { isIntersecting, ref } = useIntersectionObserver({
    root: null,
    rootMargin: "0px",
    threshold: 0.1,
    freezeOnceVisible: true,
  })

  useGSAP(
    () => {
      const tl = gsap.timeline({ paused: true })

      tl.add(
        gsap.fromTo(".shape-intro path", { drawSVG: "0%" }, { drawSVG: "100%", duration: 1.5, ease: "sine.out" }),
        0,
      )
      tl.add(gsap.fromTo(".shape-intro-text", { opacity: 0 }, { opacity: 1, duration: 1.5, ease: "sine.out" }), 0)

      tl.add(
        gsap.fromTo(
          ".shape-nav path",
          {
            drawSVG: "0%",
          },
          {
            drawSVG: "100%",
            duration: 1.2,
            ease: "sine.out",
            stagger: 0.15,
          },
        ),
        0.5,
      )
      tl.add(
        gsap.fromTo(".shape-text", { opacity: 0 }, { opacity: 1, duration: 1.2, ease: "sine.out", stagger: 0.15 }),
        0.5,
      )

      if (isIntersecting) {
        tl.play()
      }

      return () => {
        tl?.revert().kill()
      }
    },
    {
      scope: outerRef,
      dependencies: [isIntersecting],
    },
  )

  const goToSection = (id: string) => {
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  const maxScale = 1.22

  return (
    <div ref={outerRef} className="w-full my-10">
      <ScrollReveal>
        <div ref={ref} className="w-full">
          <Themed theme="black" className="w-full py-10 relative isolate themed-fg">
            {/* Background color */}
            <div className="themed-bg absolute inset-0 -z-10" data-transition="fade" />

            {/* Content */}
            <div className={cn("w-full max-w-grid mx-auto flex")}>
              <div className={cn("w-full flex flex-wrap md:flex-nowrap gap-7")}>
                <div className={cn("w-full md:w-[55%] md:shrink-0 relative")} data-transition="scale">
                  <svg
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0, 0, 674, 514"
                    className="shape-intro w-full aspect-[674/514]"
                  >
                    <path
                      d="M1,332 L1,257 L1,49 C1,22.49 22.49,1 49,1 L625,1 C651.51,1 673,22.49 673,49 L673,365 C673,391.51 651.51,413 625,413 L361,413 C334.49,413 313,434.49 313,461 L313,465 C313,491.51 291.51,513 265,513 L49,513 C22.49,513 1,491.51 1,465 L1,332 z"
                      fillOpacity="0"
                      stroke="#E5E8DB"
                      strokeWidth="2"
                    />
                  </svg>
                  {/* mobile */}
                  <EditableText
                    as="p"
                    store="title"
                    className="shape-intro-text type-title-m absolute !my-0 md:hidden"
                    placeholder="Enter a title"
                    style={{
                      fontSize: `6.5vw`,
                      width: `82vw`,
                      left: `4vw`,
                      top: `4vw`,
                    }}
                  />
                  {/* desktop */}
                  <EditableText
                    as="p"
                    store="title"
                    className="shape-intro-text type-title-l absolute !my-0 hidden md:block"
                    placeholder="Enter a title"
                    style={{
                      fontSize: `calc(min(4vw, ${52 * maxScale}px))`,
                      width: `calc(min(39.5vw, ${512 * maxScale}px))`,
                      left: `calc(min(3vw, ${40 * maxScale}px))`,
                      top: `calc(min(3vw, ${40 * maxScale}px))`,
                    }}
                  />
                </div>
                <div className={cn("w-full relative")} data-transition="scale">
                  <svg
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0, 0, 514, 514"
                    className="shape-nav w-full aspect-square"
                  >
                    <g>
                      <path
                        d="M1,33 C1,15.327 15.327,1 33,1 L385,1 C402.673,1 417,15.327 417,33 L417,49 C417,75.51 438.49,97 465,97 L481,97 C498.673,97 513,111.327 513,129 L513,161 C513,178.673 498.673,193 481,193 L313,193 C295.327,193 281,178.673 281,161 L281,121 C281,107.745 270.255,97 257,97 L33,97 C15.327,97 1,82.673 1,65 L1,33 z"
                        className="md:hover:opacity-100 opacity-0 transition-opacity duration-500 cursor-pointer pointer-events-auto"
                        style={{
                          fill: `rgb(${THEMES[((blocks[0]?.props as any)?.pageTheme as keyof typeof THEMES) || "black"]?.bg})`,
                        }}
                        onClick={() => goToSection(`section-${slugify((blocks[0]?.props as any)?.title || "")}`)}
                        onMouseOver={() => setHovered(0)}
                        onMouseOut={() => setHovered(null)}
                      />
                      <path
                        d="M1,33 C1,15.327 15.327,1 33,1 L385,1 C402.673,1 417,15.327 417,33 L417,49 C417,75.51 438.49,97 465,97 L481,97 C498.673,97 513,111.327 513,129 L513,161 C513,178.673 498.673,193 481,193 L313,193 C295.327,193 281,178.673 281,161 L281,121 C281,107.745 270.255,97 257,97 L33,97 C15.327,97 1,82.673 1,65 L1,33 z"
                        className="pointer-events-none"
                        fillOpacity="0"
                        stroke="#E5E8DB"
                        strokeWidth="2"
                      />
                    </g>
                    <g>
                      <path
                        d="M1,121 C1,107.745 11.745,97 25,97 L257,97 C270.255,97 281,107.745 281,121 L281,169 C281,182.255 270.255,193 257,193 L177,193 C163.745,193 153,203.745 153,217 L153,265 C153,278.255 142.255,289 129,289 L25,289 C11.745,289 1,278.255 1,265 L1,121 z"
                        className="md:hover:opacity-100 opacity-0 transition-opacity duration-500 cursor-pointer pointer-events-auto"
                        style={{
                          fill: `rgb(${THEMES[((blocks[1]?.props as any)?.pageTheme as keyof typeof THEMES) || "black"]?.bg})`,
                        }}
                        onClick={() => goToSection(`section-${slugify((blocks[1]?.props as any)?.title || "")}`)}
                        onMouseOver={() => setHovered(1)}
                        onMouseOut={() => setHovered(null)}
                      />
                      <path
                        d="M1,121 C1,107.745 11.745,97 25,97 L257,97 C270.255,97 281,107.745 281,121 L281,169 C281,182.255 270.255,193 257,193 L177,193 C163.745,193 153,203.745 153,217 L153,265 C153,278.255 142.255,289 129,289 L25,289 C11.745,289 1,278.255 1,265 L1,121 z"
                        className="pointer-events-none"
                        fillOpacity="0"
                        stroke="#E5E8DB"
                        strokeWidth="2"
                      />
                    </g>
                    <g>
                      <path
                        d="M153,217 C153,203.745 163.745,193 177,193 L497,193 C505.837,193 513,200.163 513,209 L513,313 C513,321.837 505.837,329 497,329 L273,329 C264.163,329 257,321.837 257,313 L257,305 C257,296.163 249.837,289 241,289 L169,289 C160.163,289 153,281.837 153,273 L153,217 z"
                        className="md:hover:opacity-100 opacity-0 transition-opacity duration-500 cursor-pointer pointer-events-auto"
                        style={{
                          fill: `rgb(${THEMES[((blocks[2]?.props as any)?.pageTheme as keyof typeof THEMES) || "black"]?.bg})`,
                        }}
                        onClick={() => goToSection(`section-${slugify((blocks[2]?.props as any)?.title || "")}`)}
                        onMouseOver={() => setHovered(2)}
                        onMouseOut={() => setHovered(null)}
                      />
                      <path
                        d="M153,217 C153,203.745 163.745,193 177,193 L497,193 C505.837,193 513,200.163 513,209 L513,313 C513,321.837 505.837,329 497,329 L273,329 C264.163,329 257,321.837 257,313 L257,305 C257,296.163 249.837,289 241,289 L169,289 C160.163,289 153,281.837 153,273 L153,217 z"
                        className="pointer-events-none"
                        fillOpacity="0"
                        stroke="#E5E8DB"
                        strokeWidth="2"
                      />
                    </g>
                    <g>
                      <path
                        d="M1,305 C1,296.163 8.163,289 17,289 L241,289 C249.837,289 257,296.163 257,305 L257,313 C257,321.837 264.163,329 273,329 L305,329 C313.837,329 321,336.163 321,345 L321,401 C321,409.837 313.837,417 305,417 L17,417 C8.163,417 1,409.837 1,401 L1,305 z"
                        className="md:hover:opacity-100 opacity-0 transition-opacity duration-500 cursor-pointer pointer-events-auto"
                        style={{
                          fill: `rgb(${THEMES[((blocks[3]?.props as any)?.pageTheme as keyof typeof THEMES) || "black"]?.bg})`,
                        }}
                        onClick={() => goToSection(`section-${slugify((blocks[3]?.props as any)?.title || "")}`)}
                        onMouseOver={() => setHovered(3)}
                        onMouseOut={() => setHovered(null)}
                      />
                      <path
                        d="M1,305 C1,296.163 8.163,289 17,289 L241,289 C249.837,289 257,296.163 257,305 L257,313 C257,321.837 264.163,329 273,329 L305,329 C313.837,329 321,336.163 321,345 L321,401 C321,409.837 313.837,417 305,417 L17,417 C8.163,417 1,409.837 1,401 L1,305 z"
                        className="pointer-events-none"
                        fillOpacity="0"
                        stroke="#E5E8DB"
                        strokeWidth="2"
                      />
                    </g>

                    <g>
                      <path
                        d="M321,353 C321,339.745 331.745,329 345,329 L385,329 L489,329 C502.255,329 513,339.745 513,353 L513,489 C513,502.255 502.255,513 489,513 L409,513 C395.745,513 385,502.255 385,489 L385,441 C385,427.745 374.255,417 361,417 L345,417 C331.745,417 321,406.255 321,393 L321,353 z"
                        className="md:hover:opacity-100 opacity-0 transition-opacity duration-500 cursor-pointer pointer-events-auto"
                        style={{
                          fill: `rgb(${THEMES[((blocks[4]?.props as any)?.pageTheme as keyof typeof THEMES) || "black"]?.bg})`,
                        }}
                        onClick={() => goToSection(`section-${slugify((blocks[4]?.props as any)?.title || "")}`)}
                        onMouseOver={() => setHovered(4)}
                        onMouseOut={() => setHovered(null)}
                      />
                      <path
                        d="M321,353 C321,339.745 331.745,329 345,329 L385,329 L489,329 C502.255,329 513,339.745 513,353 L513,489 C513,502.255 502.255,513 489,513 L409,513 C395.745,513 385,502.255 385,489 L385,441 C385,427.745 374.255,417 361,417 L345,417 C331.745,417 321,406.255 321,393 L321,353 z"
                        className="pointer-events-none"
                        fillOpacity="0"
                        stroke="#E5E8DB"
                        strokeWidth="2"
                      />
                    </g>
                    <g>
                      <path
                        d="M1,449 C1,431.327 15.327,417 33,417 L361,417 C374.255,417 385,427.745 385,441 L385,481 C385,498.673 370.673,513 353,513 L33,513 C15.327,513 1,498.673 1,481 L1,449 z"
                        className="md:hover:opacity-100 opacity-0 transition-opacity duration-500 cursor-pointer pointer-events-auto"
                        style={{
                          fill: `rgb(${THEMES[((blocks[5]?.props as any)?.pageTheme as keyof typeof THEMES) || "black"]?.bg})`,
                        }}
                        onClick={() => goToSection(`section-${slugify((blocks[5]?.props as any)?.title || "")}`)}
                        onMouseOver={() => setHovered(5)}
                        onMouseOut={() => setHovered(null)}
                      />
                      <path
                        d="M1,449 C1,431.327 15.327,417 33,417 L361,417 C374.255,417 385,427.745 385,441 L385,481 C385,498.673 370.673,513 353,513 L33,513 C15.327,513 1,498.673 1,481 L1,449 z"
                        className="pointer-events-none"
                        fillOpacity="0"
                        stroke="#E5E8DB"
                        strokeWidth="2"
                      />
                    </g>
                    <g>
                      <path
                        d="M465,1 L465,1 C491.51,1 513,22.49 513,49 L513,49 C513,75.51 491.51,97 465,97 L465,97 C438.49,97 417,75.51 417,49 L417,49 C417,22.49 438.49,1 465,1 z"
                        className="pointer-events-none"
                        style={{
                          fill: `rgb(${THEMES["black"]?.bg})`,
                        }}
                      />
                      <path
                        d="M465,1 L465,1 C491.51,1 513,22.49 513,49 L513,49 C513,75.51 491.51,97 465,97 L465,97 C438.49,97 417,75.51 417,49 L417,49 C417,22.49 438.49,1 465,1 z"
                        className="pointer-events-none"
                        fillOpacity="0"
                        stroke="#E5E8DB"
                        strokeWidth="2"
                      />
                    </g>
                  </svg>

                  {blocks.map((block, bx) => {
                    const dims = [
                      {
                        x: 24,
                        y: 24,
                        w: 300,
                      },
                      {
                        x: 24,
                        y: 120,
                        w: 280 - 24 - 24,
                      },
                      {
                        x: 172,
                        y: 332 - 116,
                        w: 360 - 24 - 24,
                      },
                      {
                        x: 24,
                        y: 428 - 116,
                        w: 200,
                      },
                      {
                        x: 352,
                        y: 468 - 116,
                        w: 120,
                      },
                      {
                        x: 24,
                        y: 556 - 116,
                        w: 384 - 24 - 24,
                      },
                    ]
                    const dim = dims[bx] || { x: 0, y: 0, w: 0 }
                    const theme = (block.props as any)?.pageTheme || "black"

                    return (
                      <Fragment key={bx}>
                        {/* mobile */}
                        <p
                          className="shape-text type-title-xs absolute pointer-events-none md:hidden"
                          style={{
                            fontSize: `calc(4vw)`,
                            lineHeight: "110%",
                            left: `calc(${dim.x / 520} * (100vw - 32px))`,
                            top: `calc(${dim.y / 520} * (100vw - 32px))`,
                            width: `calc(${dim.w / 520} * (100vw - 32px))`,
                          }}
                        >
                          {(block.props as any).title}
                        </p>
                        {/* desktop */}
                        <p
                          className={cn(
                            "shape-text type-title-s absolute pointer-events-none hidden md:block transition-colors duration-500",
                            hovered === bx && "text-[var(--theme-fg)]",
                          )}
                          style={{
                            "--theme-fg": `rgb(${THEMES[theme as keyof typeof THEMES]?.fg})`,
                            fontSize: `calc(min(1.85vw, ${24 * maxScale}px))`,
                            left: `calc(min(${(dim.x / 1296) * 100}vw, ${dim.x * maxScale}px))`,
                            top: `calc(min(${(dim.y / 1296) * 100}vw, ${dim.y * maxScale}px))`,
                            width: `calc(min(${(dim.w / 1296) * 100}vw, ${dim.w * maxScale}px))`,
                          }}
                        >
                          {(block.props as any).title}
                        </p>
                      </Fragment>
                    )
                  })}
                </div>
              </div>
            </div>
          </Themed>
        </div>
      </ScrollReveal>
      <div className={cn("w-full")}>
        <InnerBlocks
          allowedBlocks={["content/sticky-section"]}
          defaultBlocks={[
            ["content/sticky-section", {}],
            ["content/sticky-section", {}],
            ["content/sticky-section", {}],
            ["content/sticky-section", {}],
            ["content/sticky-section", {}],
            ["content/sticky-section", {}],
          ]}
          orientation="vertical"
        />
      </div>
    </div>
  )
})
